import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const TwoStepVerification = ({ enabled, setEnabled }) => {
    const [otpModal, setOtpModal] = useState(false);
    const [otp, setOtp] = useState("");
    const [showOtp, setShowOtp] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [loading, setLoading] = useState(false); // full-page loader
    const token = decryptToken("accessToken") || ""
    const urlmain = API_BASE_URL;
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await fetch(`${urlmain}/two-step/status/${isLoggedIn.main_id}`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                const res = await response.json();
                if (response.ok) {
                    setEnabled(res.verify);
                }
            } catch (error) {
                console.error(error);
            }
        };
        fetchStatus();
    }, []);

    const handleToggleClick = () => {
        setPendingAction(enabled ? "disable" : "enable");
        setLoading(true); // show loader
        sendOtp();
    };

    const sendOtp = async () => {
        try {
            const response = await fetch(`${urlmain}/two-step/send-otp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ user_id: isLoggedIn.main_id }),
            });
            if (response.ok) {
                setOtpModal(true);
            } else {
                toast.error("Failed to send OTP");
            }
        } catch (error) {
            toast.error("Error sending OTP");
        } finally {
            setLoading(false); // hide loader when request finishes
        }
    };

    const handleVerifyOtp = async () => {
        try {
            const response = await fetch(`${urlmain}/two-step/verify-otp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ otp, user_id: isLoggedIn.main_id }),
            });

            const res = await response.json();
            if (response.ok) {
                setEnabled(!enabled);
                toast.success(
                    `Two-step verification ${!enabled ? "enabled" : "disabled"}!`
                );
                setOtpModal(false);
                setOtp("");
            } else {
                toast.error(res.message || "Invalid OTP");
            }
        } catch (error) {
            toast.error("Something went wrong");
        }
    };

    return (
        <>
            <div className="profile-form-container">
                <div
                    className="profile-form-group full-span"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <p
                        className="profile-form-label"
                        style={{ fontSize: "16px", fontWeight: "500" }}
                    >
                        {enabled
                            ? "Your account is protected with 2-Step Verification"
                            : "Turn on 2-Step Verification"}
                    </p>

                    {/* Toggle Switch */}
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={handleToggleClick}
                            disabled={loading}
                        />
                        <span className="slider round"></span>
                    </label>
                </div>

                <div>
                    <p>
                        Prevent hackers from accessing your account with an
                        additional layer of security.
                    </p>
                </div>
            </div>

            {/* OTP Modal */}
            {otpModal && (
                <div className="setting-cart-modal">
                    <div className="setting-cart-modal-content">
                        <h3
                            className="setting-payment-modal-title"
                            style={{ textAlign: "center" }}
                        >
                            OTP Verification
                        </h3>

                        <label className="setting-payment-label">
                            Enter OTP
                        </label>
                        <div style={{ position: "relative" }}>
                            <input
                                type={showOtp ? "text" : "password"}
                                className="setting-payment-input"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleVerifyOtp();
                                }}
                            />
                            <span
                                style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                }}
                                onClick={() => setShowOtp((prev) => !prev)}
                            >
                                {showOtp ? <EyeOff /> : <Eye />}
                            </span>
                        </div>

                        <div className="setting-payment-modal-actions">
                            <button
                                className="setting-payment-save"
                                onClick={handleVerifyOtp}
                            >
                                Verify
                            </button>
                            <button
                                className="setting-payment-cancel"
                                onClick={() => setOtpModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Full Page Loader */}
            {loading && (
                <div className="full-page-loader">
                    <div className="spinner"></div>
                    <p>Sending OTP...</p>
                </div>
            )}

            {/* Styles */}
            <style>{`
                .switch { position: relative; display: inline-block; width: 40px; height: 22px; }
                .switch input { opacity: 0; width: 0; height: 0; }
                .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ccc; transition: 0.4s; border-radius: 34px; }
                .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: 0.4s; border-radius: 50%; }
                input:checked + .slider { background-color: #4caf50; }
                input:checked + .slider:before { transform: translateX(22px); }

                .full-page-loader {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(255, 255, 255, 0.8);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                }
                .spinner {
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #007bff;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    animation: spin 1s linear infinite;
                    margin-bottom: 10px;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export default TwoStepVerification;
